<template>
  <v-container fluid :class="{ 'mt-4': !$vuetify.breakpoint.mobile }" :style="$vuetify.breakpoint.mobile ? 'padding-bottom: 96px' : ''">
    <v-row align="center" class="mb-3">
      <v-col cols="12" md="auto">
        <v-tabs v-model="filterTab" @change="changeFilter" :grow="$vuetify.breakpoint.mobile">
          <v-tab :disabled="portionLoading"><v-icon left>mdi-format-list-bulleted</v-icon>{{ $t('currentRights.all') }}</v-tab>
          <v-tab :disabled="portionLoading"><v-icon left>mdi-timer-sand</v-icon>{{ $t('currentRights.actionRequired') }}</v-tab>
          <v-tab :disabled="portionLoading"><v-icon left>mdi-alert-circle-outline</v-icon>{{ $t('currentRights.doubleClaim') }}</v-tab>
        </v-tabs>
      </v-col>

      <v-col>
        <v-text-field
          :placeholder="$t('rights.search')"
          :disabled="portionLoading"
          @keydown.enter.prevent="doSearch"
          @click:append="doSearch"
          v-model="searchQuery"
          append-icon="mdi-magnify"
          filled
          rounded
          hide-details
          class="t-search-query mb-0" />
      </v-col>
    </v-row>

    <v-row v-if="!$vuetify.breakpoint.mobile" justify="end" class="mb-6">
      <v-col class="d-flex flex-wrap justify-end align-center py-0 pr-0">
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="menuScope">
            <v-tooltip top>
              <template v-slot:activator="tooltipScope">
                <v-btn v-on="{ ...menuScope.on, ...tooltipScope.on }" :disabled="portionLoading" class="mx-1 my-1 tonal-button">
                  <v-icon left>mdi-dots-vertical</v-icon>
                  <span>{{ $t('rights.columnsButton') }}</span>
                </v-btn>
              </template>
              <span>{{ $t('rights.columnsButtonTooltip') }}</span>
            </v-tooltip>
          </template>

          <v-list dense>
            <v-list-item v-for="col in additionalColumns" :key="col.value">
              <v-list-item-action>
                <v-checkbox
                  :input-value="selectedColumns.includes(col.value)"
                  @change="toggleColumnSelect(col.value)"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                {{ col.text }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-tooltip top>
          <template v-slot:activator="tooltipScope">
            <v-btn v-bind="tooltipScope.attrs" v-on="tooltipScope.on" :disabled="portionLoading" class="mx-1 my-1" color="primary" @click="addRightVisible = true">
              <v-icon left color="white">mdi-plus-circle-outline</v-icon>
              <span class="text--white">{{ $t('rights.create') }}</span>
            </v-btn>
          </template>
          <span>{{ $t('rights.createTooltip') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="tooltipScope">
            <v-btn outlined v-on="tooltipScope.on" :disabled="portionLoading" class="mx-1 my-1" @click="onEditClick">
              <v-icon left>mdi-pencil-outline</v-icon>
              <span>{{ $t('rights.edit') }}</span>
            </v-btn>
          </template>
          <span>{{ $t('rights.editTooltip') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="tooltipScope">
            <v-btn outlined v-on="tooltipScope.on" :disabled="portionLoading" class="mx-1 my-1" @click="onRevokeClick">
              <v-icon left>mdi-backup-restore</v-icon>
              <span>{{ $t('rights.revoke') }}</span>
            </v-btn>
          </template>
          <span>{{ $t('rights.revokeTooltip') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="tooltipScope">
            <v-btn outlined v-on="tooltipScope.on" :disabled="portionLoading || downloading" class="mx-1 my-1" @click="onDownloadRights">
              <v-icon v-if="downloading" left>mdi-loading mdi-spin</v-icon>
              <v-icon v-else left>mdi-tray-arrow-down</v-icon>
              <span>{{ $t('rights.download') }}</span>
            </v-btn>
          </template>
          <span>{{ $t('rights.downloadTooltip') }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <mobile-data-list
      v-if="$vuetify.breakpoint.mobile"
      v-model="checkedItems"
      :items="mobileItems"
      :loading="portionLoading"
      :items-per-page="25"
      :footer-props="{ itemsPerPageOptions: [] }"
      item-key="rowNumber"
      :headers="mobileHeaders"
      show-select
      single-expand
    >
      <template #item.type="{ item }">
        <v-icon v-if="String(item.type) === '1'" small>mdi-volume-high</v-icon>
        <v-icon v-else-if="String(item.type) === '2'" small>mdi-video</v-icon>
        <span v-else>{{ item.type }}</span>
      </template>
      <template #item.internationalCollection="{ item }">
        <v-icon small>{{ item.internationalCollection ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline' }}</v-icon>
      </template>
    </mobile-data-list>

    <RightsTable
      v-else
      :items="rightsList"
      :displayed-columns="displayedColumns"
      :checkedItems.sync="checkedItems"
      :continuos-amount="!currentRights.finished"
      :portion-loading="portionLoading"
      :total-count="currentRights.totalCount"
      @load-more="loadMoreItems"
      columnSortOrderStore="currentRightsColumnsOrder"
    />

    <div v-if="$vuetify.breakpoint.mobile" class="cr-mobile-actions">
      <v-btn color="primary" @click="addRightVisible = true">
        <v-icon left>mdi-plus-circle-outline</v-icon>
        {{ $t('rights.create') }}
      </v-btn>
      <v-menu top offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn outlined v-on="on" v-bind="attrs">
            <v-icon left>mdi-dots-vertical</v-icon>
            {{ $t('app.more') }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="onEditClick" :disabled="portionLoading">
            <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t('rights.edit') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="onRevokeClick" :disabled="portionLoading">
            <v-list-item-icon><v-icon>mdi-backup-restore</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t('rights.revoke') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="onDownloadRights" :disabled="portionLoading || downloading">
            <v-list-item-icon><v-icon>{{ downloading ? 'mdi-loading mdi-spin' : 'mdi-tray-arrow-down' }}</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t('rights.download') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-dialog v-model="editFormVisible" :max-width="$vuetify.breakpoint.mobile ? '95%' : '60%'" :persistent="!$vuetify.breakpoint.mobile">
      <EditRightForm
        v-if="editFormVisible"
        :items="editingRows"
        :actions-loading="editInProcess"
        :actions-loading-progress="editProgress.value"
        @close="onCancelEdit"
        @save="onEditRights"
      />
    </v-dialog>

    <v-dialog :width="$vuetify.breakpoint.mobile ? '95%' : '50%'" max-width="1000px" v-model="addRightVisible">
      <AddRightForm
        v-if="addRightVisible"
        @cancel="addRightVisible = false"
        @save="onCreateRightFinished"
      />
    </v-dialog>

    <v-dialog v-model="addingToStaging" :width="$vuetify.breakpoint.mobile ? '95%' : '50%'" persistent>
      <v-card>
        <v-card-title>
          Preparing rows to edit...
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            indeterminate
            color="primary"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import {mapGetters} from "vuex";
import RightsTable from "@/components/rights/RightsTable";
import EditRightForm from "@/components/rights/EditRightForm";
import MobileDataList from "@/components/common/MobileDataList";
import DebounceSearchLauncherMixin from "@/components/mixins/DebounceSearchLauncherMixin";
import RightsholderInitedDataDrivenMixin from "@/components/mixins/RightsholderInitedDataDrivenMixin";
import SelectedColumnsStore from "@/components/mixins/SelectedColumnsStoreMixin";
import downloadFile from "@/utils/downloadFile";
import CreateRightButtonMixin from "@/components/mixins/CreateRightButtonMixin";
import AddRightForm from "@/components/rights/AddRightForm";
import Right from "@/models/Right";

export default {
  name: 'CurrentRights',
  components: {AddRightForm, EditRightForm, RightsTable, MobileDataList},
  mixins: [SelectedColumnsStore,DebounceSearchLauncherMixin, RightsholderInitedDataDrivenMixin, CreateRightButtonMixin],
  data() {
    return {
      selectedColumns: [],
      selectedColumnsStore: "currentRightsColumns",
      editFormVisible: false,
      checkedItems: [],
      editingRows: [],
      addingToStaging: false,
      portionLoading: false,
      filterTab: { CurrentRights: 0, CurrentRightsActionRequired: 1, CurrentRightsDoubleClaims: 2 }[this.$route.name] ?? 0,
      editInProcess: false,
      addRightVisible: false,
      editProgress: {
        value: 0
      },
      downloading: false
    }
  },

  computed: {
    ...mapGetters('app', ['currentRightsholder']),
    currentRights() {
      return this.$store.getters["app/currentRights"];
    },

    rightsList() {
      if (!this.currentRights.list) {
        return [];
      }
      return this.currentRights.list.concat(this.currentRights.finished ? [] : [null]);
    },
    handlingItems() {
      return this.checkedItems.length ? this.checkedItems : this.currentRights.list.filter(item => item.editable);
    },
    additionalColumns() {
      const getCol = (value) => ({value, text: this.$t(`rights.columns.${value}`)})
      return ['rowNumber', 'spnr', 'version', 'year', 'labelName', 'externalId', 'bol', 'grp', 'cbskl', 'clnr', 'dur', 'dcinfo', 'rdxId'].map(getCol);
    },

    allColumns() {
      return [
        'rowNumber',
        'isrc',
        'type',
        'spnr',
        'artistName',
        'title',
        'countryCode',
        'year',
        'claimStartDate',
        'claimEndDate',
        'labelName',
        'status',
        'version',
        'externalId',
        'bol',
        'grp',
        'cbskl',
        'clnr',
        'dur',
        'dcinfo',
        'rdxId'
      ].concat(this.currentRightsholder.internationalCollectionAllowed ? ['internationalCollection'] : []);
    },

    displayedColumns() {
      return this.allColumns.filter((col) => (this.additionalColumns.some(({value}) => value === col) ? this.selectedColumns.includes(col) : true));
    },

    mobileItems() {
      return this.rightsList.filter(Boolean);
    },

    mobileHeaders() {
      const h = (value) => ({ value, text: this.$t(`rights.columns.${value}`) });
      const pinned = { title: 'primary', artistName: 'secondary', isrc: 'tertiary' };
      const stacked = new Set(['internationalCollection']);
      return this.allColumns.map(value => ({
        ...h(value),
        ...(pinned[value] ? { mobile: pinned[value] } : {}),
        ...(stacked.has(value) ? { stacked: true } : {}),
      }));
    },
  },

  watch: {
    $route(to) {
      const tabMap = { CurrentRights: 0, CurrentRightsActionRequired: 1, CurrentRightsDoubleClaims: 2 }
      const newTab = tabMap[to.name] ?? 0
      this.filterTab = newTab
      if (newTab == 2 && !this.selectedColumns.includes('dcinfo')) {
        this.selectedColumns.push('dcinfo')
      }
      this.reloadItems()
    }
  },

  methods: {
    onCreateRightFinished(data) {
      this.onCreateRight(data)
        .then(() => {
          this.addRightVisible = false;
          this.reloadItems();
        })
    },

    changeFilter() {
      const routeNames = ['CurrentRights', 'CurrentRightsActionRequired', 'CurrentRightsDoubleClaims']
      const targetRoute = routeNames[this.filterTab]
      if (this.$route.name === targetRoute) {
        if (this.filterTab == 2 && !this.selectedColumns.includes('dcinfo')) {
          this.selectedColumns.push('dcinfo')
        }
        this.reloadItems()
      } else {
        this.$router.push({ name: targetRoute })
        // reload sker via route-watcher
      }
    },

    // Override
    reloadItems() {
      this.loadRights();
    },

    // Do search
    doSearch() {
      this.loadRights();
    },

    // Override
    searchQueryUpdated() {
      // this.loadRights();
    },

    async onEditClick() {
      const items = this.handlingItems.length;
      const checked = this.checkedItems.length;

      if (checked == 0 || checked > 49) {
        const res = await this.$dialog.confirm({
          text: this.$t('currentRights.confirmEdit', [items])
        })

        if (!res) {
          return
        }
      }

      this.addingToStaging = true;
      this.$store.dispatch('app/addRightsToStaging', this.handlingItems)
        .then((result) => {
          this.editingRows = result.filter((x) => x['processing-status'] === 201)
            .map((x) => new Right({'!staged': true, ...x}));
          if (this.editingRows.length < this.handlingItems.length) {
            this.$toast(this.$t('currentRights.edit.addFailure', [this.handlingItems.length - this.editingRows.length, this.handlingItems.length]));
          }
          this.addingToStaging = false;
          //- No rows processed correctly
          if (this.editingRows.length === 0) {
            return
          }
          this.editFormVisible = true;
        })

    },

    onRevokeClick() {
      const items = this.handlingItems.length;
      this.$dialog.confirm({
        text: this.$t('currentRights.confirmRevoke', [items])
      })
        .then((revoke) => {
          if (revoke) {
            this.revokeRights();
          }
        })
    },

    revokeRights() {
      this.portionLoading = true;
      const items = this.handlingItems.map(({spnr, type}) => ({spnr, ityp: type, revoke: true}))
      this.$store.dispatch('app/revokeRights', {items})
        .then(() => {
          this.$toast(this.$t('currentRights.revoked'))
        })
        .finally(() => {
          this.portionLoading = false;
          this.reloadItems();
        })
    },

    loadMoreItems() {
      this.portionLoading = true;
      return this.$store.dispatch('app/loadRightsList', {search: this.searchQuery})
        .then(() => {
          this.portionLoading = false;
        });
    },

    loadRights() {
      if (!this.portionLoading === true) {
        this.portionLoading = true;
        if (this.$store.getters["app/currentRightsholderReady"]) {
          this.$store.dispatch('app/loadRightsList', {
            reload: true,
            search: this.searchQuery,
            action: ["", "rightaction", "DC"][this.filterTab]
          })
            .finally(() => {
              this.portionLoading = false;
            });
        }
      }
      return Promise.resolve('Already Loading')
    },

    toggleColumnSelect(col) {
      if (this.selectedColumns.includes(col)) {
        this.selectedColumns = this.selectedColumns.filter(c => c !== col);
      } else {
        this.selectedColumns.push(col);
      }
    },

    onDownloadRights() {
      this.downloading = true
      this.$store.dispatch('app/getRightsDownloadUrl',{search: this.searchQuery, action: ["", "rightaction", "DC"][this.filterTab], format: 'xlsx'})
        .then((res) => {
          downloadFile((res.filename ? res.filename : 'rights.xlsx'), res.url);
          this.downloading = false
        })
    },

    onCancelEdit() {
      this.editFormVisible = false;
      this.$store.dispatch('app/removeStagingRows', this.editingRows)
        .then(() => {
          this.editingRows = [];
        })
    },

    onEditRights(data) {

      const launchEdit = (items) => {
        this.editInProcess = true;
        this.editProgress = {value: 0};
        return this.$store.dispatch('app/editRightsList', {items, progressObservable: this.editProgress})
          .then((res) => {
            setTimeout(() => {
              this.editFormVisible = false;
              this.editInProcess = false;
              this.$toast(this.$t('rights.editForm.editedSuccessfully', [res.data.length]));
            }, 1000);
          })
          .catch((err) => {
            console.error(err);
            this.$toast(this.$t('rights.editForm.errorOnEdit'));
            this.editInProcess = false;
          })
      }

      switch (data.mode) {
        case 'list': {
          if (data.items.length) {
            launchEdit(data.items);
          } else {
            this.$toast(this.$t('rights.editForm.noItems'))
          }
          break;
        }
        case 'field': {
          if (Object.keys(data.item).length > 0) {
            const list = data.targets.map(({ityp, isrc, spnr}) => ({ityp, isrc, spnr, ...data.item}));
            launchEdit(list);
          } else {
            this.$toast(this.$t('rights.editForm.noChangesToApply'));
          }
        }
      }
    }
  },
}
</script>

<style>
.cr-mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px 16px 16px;
  background: linear-gradient(to bottom, transparent, var(--color-viewport-bg) 40%);
  z-index: 5;
  display: flex;
  gap: 8px;
}

.cr-mobile-actions .v-btn:first-child {
  flex: 1;
}

.v-tab {
  padding: 0 8px !important;
  text-transform: none !important;
  font-family: var(--font-family) !important;
  font-weight: var(--font-weight-medium) !important;
  font-size: var(--font-size-14) !important;
  line-height: var(--line-height-20) !important;
  letter-spacing: var(--letter-spacing-10) !important;
  text-align: center;
  vertical-align: middle;
}
</style>

